import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    OverlapTimelineLayout,
    buildOverlapTimeline,
    getBoundaryTransitionDurations,
    getSlideDurationMs,
    resolvePlaybackTime
} from 'ts/common/components/slideshows/slideshowOverlapTimeline';
import SlideshowPlayer, { ISlideshowPlayerProps } from './SlideshowPlayer';

export interface ISlideshowPlayerTickerProps extends Omit<ISlideshowPlayerProps, 'currentTime'> {
    /**
     * Controls playback. When false (default) the clock is paused at its current position.
     * Toggle to true to start/resume, false to pause.
     */
    isPlaying?: boolean;
    /** Called once when non-looping playback reaches the end. */
    onPlaybackEnd?: () => void;
}

/**
 * Imperative handle exposed via ref, for cases where the caller needs to control
 * the playback position directly (scrub bar, audio sync, keyboard seek).
 */
export interface ISlideshowPlayerTickerHandle {
    /** Jump playback to an arbitrary position without interrupting the RAF loop. */
    seek: (timeMs: number) => void;
}

/**
 * Always prefer this component over using SlideshowPlayer directly.
 *
 * SlideshowPlayerTicker owns the playback clock internally, driven by
 * requestAnimationFrame (RAF) — a browser API that fires just before each display
 * repaint (~60fps), syncs to the screen's refresh rate, and automatically pauses
 * when the tab is hidden. This means the rapid per-frame state updates stay
 * isolated inside this subtree: the surrounding UI (editor panels, settings, slide
 * thumbnails, etc.) does not re-render on every animation frame.
 *
 * For cases where the caller needs to control the playback position directly
 * (editor scrub bar, audio sync, seeking to a specific frame), obtain a ref to
 * this component and call `ref.current.seek(milliseconds)`. The ticker remains the
 * single owner of currentTime in all cases.
 *
 * Use the raw SlideshowPlayer only when you need a fully stateless renderer —
 * for example, rendering a frozen preview frame at a fixed time.
 */
const SlideshowPlayerTicker = forwardRef<ISlideshowPlayerTickerHandle, ISlideshowPlayerTickerProps>(
    ({ isPlaying = false, slideshow, onTimeChange, onPlaybackEnd, ...rest }, ref) => {
        const [currentTime, setCurrentTime] = useState(0);
        const rafRef = useRef<number | null>(null);
        const lastTimestampRef = useRef<number | null>(null);
        const layoutRef = useRef<OverlapTimelineLayout>(
            buildOverlapTimeline([], 0, { loop: false })
        );

        const layout = useMemo(
            () =>
                buildOverlapTimeline(
                    slideshow.slides.map(getSlideDurationMs),
                    getBoundaryTransitionDurations(slideshow),
                    { loop: slideshow.loopToFitMusic }
                ),
            [slideshow]
        );

        layoutRef.current = layout;

        useImperativeHandle(ref, () => ({
            seek: (timeMs: number) => {
                // Reset the delta reference so the next RAF tick doesn't treat
                // the gap between the seek point and the previous frame as elapsed time.
                lastTimestampRef.current = null;
                setCurrentTime(resolvePlaybackTime(timeMs, layoutRef.current));
            }
        }));

        useEffect(() => {
            if (!isPlaying) {
                if (rafRef.current !== null) {
                    cancelAnimationFrame(rafRef.current);
                    rafRef.current = null;
                }

                lastTimestampRef.current = null;
                return;
            }

            const startLayout = layoutRef.current;

            // Only runs when isPlaying becomes true — restart from the beginning after
            // the end, without resetting when layout changes mid-playback.
            if (!startLayout.loop) {
                setCurrentTime((timeMs) => (timeMs >= startLayout.totalDuration ? 0 : timeMs));
            }

            let cancelled = false;
            // Reset so the first tick doesn't compute a delta from a stale timestamp
            // (mirrors the same guard in seek()). Fixes resume-from-wrong-position.
            lastTimestampRef.current = null;

            const tick = (timestamp: number) => {
                // If the effect was cleaned up before this RAF fired, bail out
                // without scheduling the next frame (prevents the RAF loop from
                // continuing to run after a pause).
                if (cancelled) {
                    return;
                }

                if (lastTimestampRef.current !== null) {
                    const deltaMs = timestamp - lastTimestampRef.current;
                    const tickLayout = layoutRef.current;
                    let reachedEnd = false;

                    setCurrentTime((timeMs) => {
                        const nextTimeMs = timeMs + deltaMs;

                        if (!tickLayout.loop && nextTimeMs >= tickLayout.totalDuration) {
                            reachedEnd = true;
                            return tickLayout.totalDuration;
                        }

                        return resolvePlaybackTime(nextTimeMs, tickLayout);
                    });

                    if (reachedEnd) {
                        lastTimestampRef.current = null;
                        onPlaybackEnd?.();
                        return;
                    }
                }

                lastTimestampRef.current = timestamp;
                rafRef.current = requestAnimationFrame(tick);
            };

            rafRef.current = requestAnimationFrame(tick);

            return () => {
                cancelled = true;

                if (rafRef.current !== null) {
                    cancelAnimationFrame(rafRef.current);
                    rafRef.current = null;
                }

                lastTimestampRef.current = null;
            };
        }, [isPlaying, onPlaybackEnd]);

        useEffect(() => {
            setCurrentTime((timeMs) => resolvePlaybackTime(timeMs, layout));
        }, [layout]);

        useEffect(() => {
            onTimeChange?.(currentTime);
        }, [currentTime, onTimeChange]);

        return <SlideshowPlayer slideshow={slideshow} currentTime={currentTime} {...rest} />;
    }
);

SlideshowPlayerTicker.displayName = 'SlideshowPlayerTicker';

export default SlideshowPlayerTicker;
